/**
 * Shared authentication helpers for the web shell.
 *
 * Houses post-login scope resolution and the `returnTo` safe-redirect
 * validator so that login and verify routes share a single implementation.
 *
 * @module lib/auth-helpers
 */

import { defaultRedirectForScopes, type EnrollmentEntry, type MembershipEntry, resolveScopes } from '@learnai/api/auth';
import { tryCatch } from '@learnai/tools/try-catch';

// ---------------------------------------------------------------------------
// Post-login scope resolution
// ---------------------------------------------------------------------------

const DEV_API_ORIGIN = 'http://localhost:3000';
const SCOPE_FETCH_TIMEOUT_MS = 8_000;

function getApiBase(): string {
	if (typeof window === 'undefined') return '';
	const viteOrigin = import.meta.env.VITE_API_ORIGIN;
	if (typeof viteOrigin === 'string' && viteOrigin.trim() !== '') return viteOrigin.trim();
	if (window.location.hostname === 'localhost' && window.location.port === '3024') return DEV_API_ORIGIN;
	return '';
}

/**
 * After a successful token refresh, resolves the user's scopes by
 * directly fetching memberships and enrollments with the freshly-issued
 * access token and returns the best redirect destination.
 *
 * Uses direct `fetch` rather than the oRPC client because the client
 * may not yet reflect the new token.
 * When on the dev proxy (origin 3024), requests go to port 3000 so they reach the server.
 */
export async function resolvePostLoginScopes(accessToken: string): Promise<string> {
	const base = getApiBase();
	const headers = {
		Authorization: `Bearer ${accessToken}`,
		Accept: 'application/json',
	};

	const fetchesPromise = Promise.all([
		tryCatch(fetch(`${base}/api/users/me/memberships`, { headers })),
		tryCatch(fetch(`${base}/api/users/me/enrollments`, { headers })),
	]);

	const TIMEOUT = Symbol('timeout');
	const result = await Promise.race([
		fetchesPromise,
		new Promise<typeof TIMEOUT>((resolve) => setTimeout(() => resolve(TIMEOUT), SCOPE_FETCH_TIMEOUT_MS)),
	]);

	if (result === TIMEOUT) {
		return defaultRedirectForScopes([]);
	}

	const [membershipsRes, enrollmentsRes] = result;

	let memberships: MembershipEntry[] = [];
	let enrollments: EnrollmentEntry[] = [];

	if (!membershipsRes.error && membershipsRes.data?.ok) {
		const { data } = await tryCatch(membershipsRes.data.json() as Promise<{ memberships: MembershipEntry[] }>);
		if (data?.memberships) memberships = data.memberships;
	}

	if (!enrollmentsRes.error && enrollmentsRes.data?.ok) {
		const { data } = await tryCatch(enrollmentsRes.data.json() as Promise<{ enrollments: EnrollmentEntry[] }>);
		if (data?.enrollments) enrollments = data.enrollments;
	}

	const scopes = resolveScopes(memberships, enrollments);
	return defaultRedirectForScopes(scopes);
}

// ---------------------------------------------------------------------------
// Safe return-to validation
// ---------------------------------------------------------------------------

/**
 * Validates a `returnTo` path — must be a relative, same-origin path
 * starting with `/` to prevent open-redirect attacks.
 */
export function safeReturnTo(value: string | null): string | null {
	if (!value) return null;
	if (value.startsWith('/') && !value.startsWith('//')) return value;
	return null;
}
